/**
 * ACC Assessment Upload Component
 * Handles uploads of multiple files, combines them into a single report
 *
 * SECURITY: All fetch calls include JWT auth token from sessionStorage
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://clinhx-backend-v2.onrender.com/api';

// Helper to get auth headers for fetch calls
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const ACCAssessmentUpload = () => {
  const navigate = useNavigate();
  const [referralFiles, setReferralFiles] = useState([]);
  const [clinicalNotes, setClinicalNotes] = useState([]);
  const [assessmentType, setAssessmentType] = useState('auto');
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]); // Array of { jobId, fileName, status, progress, message, result, error }
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const jobsRef = useRef(jobs);
  const abortRef = useRef(null);

  // Q&A chat state
  const [chatOpen, setChatOpen] = useState(null); // assessment ID or null
  const [chatMessages, setChatMessages] = useState([]); // [{ role: 'user'|'assistant', content }]
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Keep ref in sync with state so the poll callback reads latest jobs
  // without the effect re-triggering on every poll
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  // Derive a stable boolean: are there active jobs to poll?
  // Don't start polling while still uploading — handleSubmit calls setJobs
  // in a loop and we don't want polling to race with those updates.
  const hasActiveJobs = !uploading && jobs.some(j => j.status === 'queued' || j.status === 'processing');

  // Poll for all active job statuses
  useEffect(() => {
    if (!hasActiveJobs) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // AbortController for cancelling in-flight fetches on cleanup/unmount
    const abortController = new AbortController();
    abortRef.current = abortController;

    const pollStatuses = async () => {
      const currentJobs = jobsRef.current;
      const updatedJobs = await Promise.all(
        currentJobs.map(async (job) => {
          if (job.status === 'completed' || job.status === 'failed') {
            return job;
          }
          try {
            // SECURITY: Include auth token in status polling
            const response = await fetch(`${API_URL}/acc-assessments/job/${job.jobId}/status`, {
              headers: getAuthHeaders(),
              signal: abortController.signal
            });
            const status = await response.json();
            return {
              ...job,
              status: status.status,
              progress: status.progress,
              message: status.message,
              result: status.status === 'completed' ? status.result : null,
              error: status.error
            };
          } catch (err) {
            if (err.name === 'AbortError') return job; // Cancelled on unmount
            console.error('Status poll error:', err);
            return job;
          }
        })
      );
      // Only update if not aborted
      if (!abortController.signal.aborted) {
        setJobs(updatedJobs);
      }
    };

    // Start polling at 5-second intervals
    pollStatuses(); // Initial poll
    pollIntervalRef.current = setInterval(pollStatuses, 5000);

    return () => {
      abortController.abort();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [hasActiveJobs]);

  const handleReferralChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setReferralFiles(prev => [...prev, ...newFiles]);
    // Reset the input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleNotesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setClinicalNotes(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (type === 'referral') {
      setReferralFiles(prev => [...prev, ...droppedFiles]);
    } else {
      setClinicalNotes(prev => [...prev, ...droppedFiles]);
    }
  }, []);

  const removeReferral = (index) => {
    setReferralFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeNote = (index) => {
    setClinicalNotes(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (referralFiles.length === 0) {
      setError('Please upload at least one referral document');
      return;
    }

    setUploading(true);
    setError(null);

    // Submit all referral files as a single job - they are combined into one report
    const formData = new FormData();
    referralFiles.forEach(file => formData.append('referral', file));
    clinicalNotes.forEach(file => formData.append('clinicalNotes', file));
    formData.append('assessmentType', assessmentType);

    const fileNames = referralFiles.map(f => f.name).join(', ');

    try {
      const response = await fetch(`${API_URL}/acc-assessments/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        setJobs([{
          jobId: null,
          fileName: fileNames,
          status: 'failed',
          progress: 0,
          message: data.details || data.error || 'Upload failed',
          result: null,
          error: data.details || data.error
        }]);
      } else if (data.jobId) {
        setJobs([{
          jobId: data.jobId,
          fileName: fileNames,
          status: 'queued',
          progress: 0,
          message: 'Queued for processing...',
          result: null,
          error: null
        }]);
      } else if (data.extraction) {
        setJobs([{
          jobId: null,
          fileName: fileNames,
          status: 'completed',
          progress: 100,
          message: 'Complete',
          result: data,
          error: null
        }]);
      }
    } catch (err) {
      setJobs([{
        jobId: null,
        fileName: fileNames,
        status: 'failed',
        progress: 0,
        message: err.message,
        result: null,
        error: err.message
      }]);
    }

    setUploading(false);
  };

  const handleDownload = async (job) => {
    if (!job.result?.assessment?.id) {
      setError('No assessment ID available for download');
      return;
    }

    try {
      // SECURITY: Include auth token in download request
      const response = await fetch(`${API_URL}/acc-assessments/${job.result.assessment.id}/download`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const patientName = job.result.patient?.name || 'Unknown';
      const assessType = job.result.assessment?.assessmentType || 'assessment';
      a.download = `ACC_${assessType}_${patientName.replace(/\s+/g, '_')}.docx`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  // Q&A chat functions
  const openChat = (assessmentId) => {
    setChatOpen(assessmentId);
    setChatMessages([]);
    setChatInput('');
  };

  const closeChat = () => {
    setChatOpen(null);
    setChatMessages([]);
    setChatInput('');
  };

  const sendQuestion = async () => {
    const question = chatInput.trim();
    if (!question || chatLoading || !chatOpen) return;

    const newMessages = [...chatMessages, { role: 'user', content: question }];
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await fetch(`${API_URL}/acc-assessments/${chatOpen}/ask`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question,
          history: chatMessages // Send prior messages for multi-turn context
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setChatMessages([...newMessages, { role: 'assistant', content: `Error: ${data.error || 'Failed to get answer'}` }]);
      } else {
        setChatMessages([...newMessages, { role: 'assistant', content: data.answer }]);
      }
    } catch (err) {
      setChatMessages([...newMessages, { role: 'assistant', content: `Error: ${err.message}` }]);
    }

    setChatLoading(false);
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatLoading]);

  const resetForm = () => {
    setJobs([]);
    setReferralFiles([]);
    setClinicalNotes([]);
    setError(null);
    closeChat();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Render job cards for multiple referrals
  const renderJobCards = () => {
    const completedCount = jobs.filter(j => j.status === 'completed').length;
    const failedCount = jobs.filter(j => j.status === 'failed').length;
    const completedJobs = jobs.filter(j => j.status === 'completed' && j.result?.assessment?.id);

    return (
      <div style={styles.jobsContainer}>
        <div style={styles.jobsHeader}>
          <h3 style={styles.jobsTitle}>Processing Assessment</h3>
        </div>

        <div style={styles.summary}>
          {completedCount > 0 && <span style={styles.successText}>✅ {completedCount} complete</span>}
          {failedCount > 0 && <span style={styles.errorText}>❌ {failedCount} failed</span>}
        </div>

        {jobs.map((job, index) => (
          <div key={index} style={styles.jobCard}>
            <div style={styles.jobHeader}>
              <span style={styles.jobFileName}>{job.fileName}</span>
              <span style={{
                ...styles.jobStatus,
                color: job.status === 'completed' ? '#16a34a' : 
                       job.status === 'failed' ? '#dc2626' : '#2563eb'
              }}>
                {job.status === 'completed' ? '✅' : 
                 job.status === 'failed' ? '❌' : 
                 job.status === 'processing' ? '⏳' : '📋'} {job.status}
              </span>
            </div>

            {(job.status === 'queued' || job.status === 'processing') && (
              <div style={styles.progressSection}>
                <div style={styles.progressBarBg}>
                  <div style={{ ...styles.progressBarFill, width: `${job.progress}%` }} />
                </div>
                <span style={styles.progressText}>{job.message} ({Math.round(job.progress)}%)</span>
              </div>
            )}

            {job.status === 'completed' && job.result && (
              <div style={styles.jobResult}>
                <span style={styles.patientName}>
                  {job.result.patient?.name || 'Unknown'} - {job.result.extraction?.coveredInjuries?.[0]?.injuryDescription || 'Extraction complete'}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Stage 2: add the history and examination and re-analyse,
                      without downloading and re-uploading the report. */}
                  <button
                    onClick={() => navigate(`/impairment/analysis/${job.result.assessment.id}`)}
                    style={{ ...styles.downloadButton, backgroundColor: '#4f46e5' }}
                  >
                    Add History &amp; Examination
                  </button>
                  <button onClick={() => handleDownload(job)} style={styles.downloadButton}>
                    Download Report
                  </button>
                  <button
                    onClick={() => chatOpen === job.result.assessment.id ? closeChat() : openChat(job.result.assessment.id)}
                    style={{
                      ...styles.downloadButton,
                      backgroundColor: chatOpen === job.result.assessment.id ? '#dc2626' : '#059669'
                    }}
                  >
                    {chatOpen === job.result.assessment.id ? 'Close Q&A' : 'Ask Questions'}
                  </button>
                </div>
              </div>
            )}

            {job.status === 'failed' && (
              <div style={styles.jobError}>
                {job.error || 'Processing failed'}
              </div>
            )}
          </div>
        ))}

        {/* Q&A Chat Panel */}
        {chatOpen && (
          <div style={styles.chatPanel}>
            <div style={styles.chatHeader}>
              <h4 style={styles.chatTitle}>Document Q&A</h4>
              <button onClick={closeChat} style={styles.chatCloseButton}>✕</button>
            </div>

            <div style={styles.chatMessages}>
              {chatMessages.length === 0 && (
                <div style={styles.chatWelcome}>
                  Ask any question about the assessment documents. For example:
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>What page was the eye assessment on?</li>
                    <li>Summarise the chronological medical history</li>
                    <li>What were the covered injuries and their dates?</li>
                    <li>Were there any previous impairment assessments?</li>
                  </ul>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} style={msg.role === 'user' ? styles.chatUserMsg : styles.chatAssistantMsg}>
                  <div style={styles.chatMsgLabel}>{msg.role === 'user' ? 'You' : 'AI'}</div>
                  <div style={styles.chatMsgContent}>{msg.content}</div>
                </div>
              ))}
              {chatLoading && (
                <div style={styles.chatAssistantMsg}>
                  <div style={styles.chatMsgLabel}>AI</div>
                  <div style={{ ...styles.chatMsgContent, color: '#9ca3af' }}>Thinking...</div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div style={styles.chatInputRow}>
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
                placeholder="Ask a question about the documents..."
                style={styles.chatTextarea}
                rows={2}
                disabled={chatLoading}
              />
              <button
                onClick={sendQuestion}
                disabled={chatLoading || !chatInput.trim()}
                style={{
                  ...styles.chatSendButton,
                  opacity: (chatLoading || !chatInput.trim()) ? 0.5 : 1
                }}
              >
                Send
              </button>
            </div>
          </div>
        )}

        <div style={styles.actions}>
          <button onClick={resetForm} style={styles.newUploadButton}>
            Upload More Referrals
          </button>
        </div>
      </div>
    );
  };

  // Render upload form
  const renderUploadForm = () => {
    return (
      <div style={styles.uploadArea}>
        {/* Referrals drop zone */}
        <div 
          style={styles.dropZone} 
          onDrop={(e) => handleDrop(e, 'referral')} 
          onDragOver={(e) => e.preventDefault()}
        >
          <span style={styles.dropIcon}>📄</span>
          <h3>ACC Referral Documents</h3>
          <p>Drag & drop or click to upload (PDF, DOCX)</p>
          <p style={styles.multipleHint}>Upload multiple files to combine into one report</p>
          <input 
            type="file" 
            accept=".pdf,.docx" 
            multiple
            onChange={handleReferralChange} 
            style={styles.fileInput} 
          />
        </div>

        {/* Selected referrals list */}
        {referralFiles.length > 0 && (
          <div style={styles.fileList}>
            <div style={styles.fileListHeader}>
              <h4 style={styles.fileListTitle}>Selected Referrals ({referralFiles.length})</h4>
              <button onClick={() => setReferralFiles([])} style={styles.clearAllButton}>Clear All</button>
            </div>
            {referralFiles.map((file, index) => (
              <div key={index} style={styles.fileItem}>
                <span>📄 {file.name} ({formatFileSize(file.size)})</span>
                <button onClick={() => removeReferral(index)} style={styles.removeButton}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Clinical notes drop zone */}
        <div 
          style={styles.dropZone} 
          onDrop={(e) => handleDrop(e, 'notes')} 
          onDragOver={(e) => e.preventDefault()}
        >
          <span style={styles.dropIcon}>📋</span>
          <h3>Clinical Notes (Optional)</h3>
          <p>Shared across all referrals</p>
          <input 
            type="file" 
            accept=".pdf,.docx" 
            multiple 
            onChange={handleNotesChange} 
            style={styles.fileInput} 
          />
          {clinicalNotes.length > 0 && (
            <div style={styles.fileName}>✓ {clinicalNotes.length} file(s) selected</div>
          )}
        </div>

        {/* Assessment type selector */}
        <div style={styles.typeSelector}>
          <label style={styles.label}>Assessment Type:</label>
          <select 
            value={assessmentType} 
            onChange={(e) => setAssessmentType(e.target.value)} 
            style={styles.select}
          >
            <option value="auto">Auto-detect</option>
            <option value="physical">Physical Injury</option>
            <option value="mental_injury">Mental Injury</option>
            <option value="tbi">Traumatic Brain Injury</option>
            <option value="combined">Combined Physical & Mental Injury</option>
          </select>
        </div>

        {error && <div style={styles.error}>❌ {error}</div>}

        <button 
          onClick={handleSubmit} 
          disabled={uploading || referralFiles.length === 0} 
          style={{
            ...styles.submitButton, 
            opacity: (uploading || referralFiles.length === 0) ? 0.6 : 1
          }}
        >
          {uploading ? '⏳ Uploading...' : `🚀 Process ${referralFiles.length || 0} File${referralFiles.length !== 1 ? 's' : ''} into Report`}
        </button>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <button
        onClick={() => navigate('/dashboard')}
        style={styles.backButton}
        onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
        onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
        Back to Dashboard
      </button>
      <div style={styles.header}>
        <h1 style={styles.title}>ACC Assessment Upload</h1>
        <p style={styles.subtitle}>Upload ACC referral documents for automatic extraction</p>
        <p style={styles.supportNote}>✅ Upload multiple files into one report • Documents up to 500+ pages with OCR</p>
      </div>

      {jobs.length > 0 ? renderJobCards() : renderUploadForm()}
    </div>
  );
};

// Styles
const styles = {
  container: { maxWidth: '1000px', margin: '0 auto', padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  backButton: { display: 'flex', alignItems: 'center', color: '#4b5563', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', marginBottom: '8px', fontSize: '14px', padding: '0' },
  header: { textAlign: 'center', marginBottom: '32px' },
  title: { fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' },
  subtitle: { fontSize: '16px', color: '#666', margin: 0 },
  supportNote: { fontSize: '14px', color: '#16a34a', marginTop: '8px' },
  
  uploadArea: { display: 'flex', flexDirection: 'column', gap: '20px' },
  dropZone: { border: '2px dashed #ccc', borderRadius: '12px', padding: '32px', textAlign: 'center', cursor: 'pointer', backgroundColor: '#fafafa', position: 'relative' },
  dropIcon: { fontSize: '48px', display: 'block', marginBottom: '12px' },
  fileInput: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' },
  fileName: { marginTop: '12px', color: '#16a34a', fontWeight: '600' },
  multipleHint: { fontSize: '12px', color: '#888', marginTop: '4px' },
  
  fileList: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' },
  fileListHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  fileListTitle: { margin: '0', fontSize: '14px', fontWeight: '600', color: '#374151' },
  clearAllButton: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', fontWeight: '500', padding: '4px 8px' },
  fileItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#fff', borderRadius: '6px', marginBottom: '8px', fontSize: '14px' },
  removeButton: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: '4px 8px' },
  
  typeSelector: { display: 'flex', alignItems: 'center', gap: '12px' },
  label: { fontWeight: '600', color: '#333' },
  select: { padding: '10px 16px', border: '2px solid #ddd', borderRadius: '8px', fontSize: '16px' },
  submitButton: { padding: '16px 32px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  error: { backgroundColor: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', border: '1px solid #fecaca' },

  // Jobs display
  jobsContainer: { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  jobsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  jobsTitle: { fontSize: '20px', fontWeight: '600', margin: 0 },
  summary: { display: 'flex', gap: '16px', marginBottom: '16px', fontSize: '14px' },
  successText: { color: '#16a34a', fontWeight: '600' },
  errorText: { color: '#dc2626', fontWeight: '600' },

  jobCard: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', marginBottom: '12px' },
  jobHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  jobFileName: { fontWeight: '600', fontSize: '14px', color: '#1f2937' },
  jobStatus: { fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' },
  
  progressSection: { marginTop: '8px' },
  progressBarBg: { height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' },
  progressBarFill: { height: '100%', backgroundColor: '#2563eb', borderRadius: '4px', transition: 'width 0.3s ease' },
  progressText: { fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' },

  jobResult: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap', gap: '8px' },
  patientName: { fontSize: '14px', color: '#374151' },
  downloadButton: { padding: '8px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  
  jobError: { marginTop: '8px', fontSize: '13px', color: '#dc2626', backgroundColor: '#fef2f2', padding: '8px 12px', borderRadius: '6px' },

  actions: { display: 'flex', justifyContent: 'center', marginTop: '20px' },
  newUploadButton: { padding: '12px 24px', backgroundColor: '#fff', color: '#2563eb', border: '2px solid #2563eb', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

  // Q&A Chat
  chatPanel: { marginTop: '20px', border: '2px solid #059669', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' },
  chatHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: '#059669', color: '#fff' },
  chatTitle: { margin: 0, fontSize: '16px', fontWeight: '600' },
  chatCloseButton: { background: 'none', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '4px 8px' },
  chatMessages: { maxHeight: '400px', overflowY: 'auto', padding: '16px' },
  chatWelcome: { color: '#6b7280', fontSize: '14px', lineHeight: '1.5', padding: '8px 0' },
  chatUserMsg: { marginBottom: '12px', padding: '10px 14px', backgroundColor: '#eff6ff', borderRadius: '8px', borderLeft: '3px solid #2563eb' },
  chatAssistantMsg: { marginBottom: '12px', padding: '10px 14px', backgroundColor: '#f0fdf4', borderRadius: '8px', borderLeft: '3px solid #059669' },
  chatMsgLabel: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px' },
  chatMsgContent: { fontSize: '14px', lineHeight: '1.6', color: '#1f2937', whiteSpace: 'pre-wrap' },
  chatInputRow: { display: 'flex', gap: '8px', padding: '12px 16px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb' },
  chatTextarea: { flex: 1, padding: '10px 12px', border: '2px solid #d1d5db', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'none', outline: 'none' },
  chatSendButton: { padding: '10px 20px', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-end' }
};

export default ACCAssessmentUpload;
